/*
  Cart drawer upsells + shipping protection.
  Uses the Cart AJAX API with the Section Rendering API so the whole drawer
  (items, upsells, protection row, totals) re-renders from Liquid after
  every change — exactly like Dawn's native cart updates.
*/
(function () {
  'use strict';

  var OPT_OUT_KEY = 'cart-shipping-protection-opt-out';
  var busy = false;
  var autoAddAttempted = false;

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function requestConfig() {
    if (typeof window.fetchConfig === 'function') return window.fetchConfig('javascript');
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
    };
  }

  function sectionsPayload() {
    return {
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    };
  }

  function setCartError(message) {
    var errorsEl = document.getElementById('CartDrawer-CartErrors');
    if (errorsEl) errorsEl.textContent = message || '';
  }

  function publishCartUpdate(state) {
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsells', cartData: state });
    }
  }

  function toggleEmptyClasses(state) {
    if (typeof state.item_count !== 'number') return;
    var drawer = getDrawer();
    if (drawer) drawer.classList.toggle('is-empty', state.item_count === 0);
  }

  /* Re-render via the drawer's own renderContents (keeps drawer open if open). */
  function render(state) {
    toggleEmptyClasses(state);
    var drawer = getDrawer();
    if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
      drawer.renderContents(state);
    }
    publishCartUpdate(state);
  }

  /* Silent re-render for background housekeeping — never opens the drawer. */
  function renderSilent(state) {
    toggleEmptyClasses(state);
    if (!state.sections) return;
    var drawer = getDrawer();

    var drawerHtml = state.sections['cart-drawer'];
    var target = document.getElementById('CartDrawer');
    if (drawerHtml && target) {
      var parsed = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
      if (parsed) {
        target.innerHTML = parsed.innerHTML;
        var overlay = target.querySelector('#CartDrawer-Overlay');
        if (overlay && drawer) overlay.addEventListener('click', drawer.close.bind(drawer));
      }
    }

    var bubbleHtml = state.sections['cart-icon-bubble'];
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubble) {
      var parsedBubble = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
      if (parsedBubble) bubble.innerHTML = parsedBubble.innerHTML;
    }

    publishCartUpdate(state);
  }

  function addVariant(variantId, options) {
    options = options || {};
    if (busy || !variantId) return Promise.resolve();
    busy = true;

    var body = Object.assign({ items: [{ id: variantId, quantity: 1 }] }, sectionsPayload());

    return fetch(window.routes.cart_add_url, Object.assign(requestConfig(), { body: JSON.stringify(body) }))
      .then(function (response) {
        return response.json();
      })
      .then(function (state) {
        if (state.status || state.errors) {
          setCartError(state.description || state.message || state.errors || '');
          return false;
        }
        setCartError('');
        (options.silent ? renderSilent : render)(state);
        return true;
      })
      .catch(function (error) {
        console.error('[cart-upsells] add failed', error);
        return false;
      })
      .finally(function () {
        busy = false;
      });
  }

  function removeLine(lineKey, options) {
    options = options || {};
    if (busy || !lineKey) return Promise.resolve();
    busy = true;

    var body = Object.assign({ id: lineKey, quantity: 0 }, sectionsPayload());

    return fetch(window.routes.cart_change_url, Object.assign(requestConfig(), { body: JSON.stringify(body) }))
      .then(function (response) {
        return response.json();
      })
      .then(function (state) {
        if (state.status || state.errors) {
          setCartError(state.description || state.message || state.errors || '');
          return false;
        }
        setCartError('');
        (options.silent ? renderSilent : render)(state);
        return true;
      })
      .catch(function (error) {
        console.error('[cart-upsells] remove failed', error);
        return false;
      })
      .finally(function () {
        busy = false;
      });
  }

  /* ---------- Upsell "Add" buttons (event delegation survives re-renders) ---------- */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('.cart-upsell__add');
    if (!button) return;
    event.preventDefault();
    if (busy || button.hasAttribute('disabled')) return;

    button.classList.add('loading');
    button.setAttribute('disabled', '');

    addVariant(Number(button.dataset.variantId)).finally(function () {
      /* Button usually no longer exists after re-render; reset just in case. */
      button.classList.remove('loading');
      button.removeAttribute('disabled');
    });
  });

  /* ---------- Shipping protection toggle ---------- */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches('.cart-protection__checkbox')) return;

    var wrapper = input.closest('[data-cart-protection]');
    if (!wrapper) return;
    if (busy) {
      input.checked = !input.checked;
      return;
    }
    wrapper.classList.add('is-busy');
    var turningOn = input.checked;

    try {
      if (turningOn) sessionStorage.removeItem(OPT_OUT_KEY);
      else sessionStorage.setItem(OPT_OUT_KEY, '1');
    } catch (e) {
      /* noop */
    }

    var operation = turningOn
      ? addVariant(Number(wrapper.dataset.variantId))
      : removeLine(wrapper.dataset.lineKey);

    operation.then(function (succeeded) {
      /* On success the footer re-renders and this wrapper is replaced.
         On failure, revert the switch so it reflects the real cart state. */
      if (!succeeded && document.contains(wrapper)) {
        input.checked = !turningOn;
        wrapper.classList.remove('is-busy');
      }
    });
  });

  /* ---------- Housekeeping ----------
     1. If shipping protection is the only thing left in the cart, remove it.
     2. Auto-add protection when enabled and the cart has items. */
  function housekeeping() {
    if (busy) return;
    var wrapper = document.querySelector('[data-cart-protection]');
    if (!wrapper) return;

    var itemCount = parseInt(wrapper.dataset.itemCount, 10) || 0;
    var protectionQty = parseInt(wrapper.dataset.protectionQty, 10) || 0;
    var inCart = protectionQty > 0;
    var onlyProtection = inCart && itemCount === protectionQty;

    if (onlyProtection) {
      removeLine(wrapper.dataset.lineKey, { silent: true });
      return;
    }

    var optedOut = false;
    try {
      optedOut = sessionStorage.getItem(OPT_OUT_KEY) === '1';
    } catch (e) {
      /* noop */
    }

    if (
      wrapper.dataset.autoAdd === 'true' &&
      !inCart &&
      itemCount > 0 &&
      !optedOut &&
      !autoAddAttempted
    ) {
      autoAddAttempted = true;
      addVariant(Number(wrapper.dataset.variantId), { silent: true });
    }
  }

  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'cart-upsells') return;
      /* Wait for Dawn to finish swapping section HTML before reading fresh state. */
      setTimeout(housekeeping, 350);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', housekeeping);
  } else {
    housekeeping();
  }
})();
